/**
 * Turning an address on file into a link that opens the device's map app.
 *
 * A cleaner reading a visit does not want the address as text; they want it as
 * directions. Every place this app shows an address on its own line therefore
 * shows it as a link, and this module decides where that link points.
 *
 * Two decisions live here, and both are deliberately small:
 *
 *   * **Which map.** Apple devices get Apple Maps and everything else gets
 *     Google Maps, because on iOS, iPadOS and macOS a `maps.apple.com` URL is
 *     handed straight to the Maps app the person already uses, while the same
 *     link on Android or a desktop browser is a dead end. Nothing here asks
 *     anyone to choose — a preference nobody set is a preference nobody wants,
 *     and the platform already expresses one.
 *   * **Which point.** A property picked from address autocomplete carries the
 *     coordinates the provider gave for it, and those are used in preference
 *     to the text. For an address that geocodes cleanly they are the same
 *     building either way; where they differ is precisely where the text would
 *     have sent somebody to the wrong place — an ambiguous street, a rural
 *     route, a typo that still geocodes to *something*.
 *
 * Most of this app never has coordinates to offer: quotes, visits and invoices
 * carry the address as a snapshot string the Worker formatted when the record
 * was written, and that string is all there is. So both entry points below
 * accept either shape, and a bare string is a first-class input rather than a
 * degraded one.
 */

/** The address fields the structured records in this app carry. */
export interface MappableAddress {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  /*
    No `countryCode`, deliberately, though `Property` carries one.

    It would be the right field to add — the country is the one part of an
    address a search cannot guess, and "10 King Street, London" with no country
    resolves to whichever King Street is nearest whoever is searching. But
    `createPropertySchema` defaults it to 'US' and the property form never
    sends it, so what is stored is the default rather than a statement about
    where the property is. Appending it would confidently send a hand-typed
    London address to the United States, which is worse than the ambiguity it
    was meant to fix.

    The fix is upstream: have property creation inherit the company's or
    customer's country. Once the column means something, it belongs here.
  */
  /** Where the address actually is, when it was picked from a suggestion. */
  latitude?: number | null;
  longitude?: number | null;
}

/** Either shape an address reaches the UI in. */
export type AddressInput = MappableAddress | string | null | undefined;

/**
 * The address as one line, the way it would be said out loud.
 *
 * The Worker's `formatPropertyAddress` joins its parts with newlines so an
 * address can be printed as a block, which is right on a quote and wrong in a
 * map query — a newline in a search string is at best ignored and at worst
 * truncates it. Both shapes therefore collapse to the same single line here,
 * with empty parts dropped rather than left as stray commas.
 *
 * Returns '' when there is no address at all, which is the signal every caller
 * uses to render plain text instead of a link.
 */
export function formatAddressLine(address: AddressInput): string {
  if (!address) return '';
  if (typeof address === 'string') {
    return address
      .split('\n')
      .map((part) => part.trim())
      .filter(Boolean)
      .join(', ');
  }
  const street = [address.addressLine1, address.addressLine2].map(trimmed).filter(Boolean).join(' ');
  // "Springfield, IL 62704" — the state and postcode belong together without a
  // comma between them, and either can be missing on its own.
  const region = [trimmed(address.state), trimmed(address.postalCode)].filter(Boolean).join(' ');
  return [street, trimmed(address.city), region].filter(Boolean).join(', ');
}

/**
 * The URL to open for an address, or null when there is nothing to open.
 *
 * Null rather than a link to nowhere: an empty search opens the map app on
 * whatever it happened to be showing last, which reads as this app having lost
 * the address.
 *
 * `userAgent` is what decides Apple Maps against Google Maps; omitting it —
 * as server rendering must — gets the Google link, which works in every
 * browser rather than only in some.
 */
export function mapsUrl(address: AddressInput, userAgent?: string | null): string | null {
  const line = formatAddressLine(address);
  const point = address && typeof address !== 'string' ? coordinates(address) : null;
  if (!line && !point) return null;

  return prefersAppleMaps(userAgent) ? appleMapsUrl(line, point) : googleMapsUrl(line, point);
}

/**
 * Whether this is a device whose Maps app answers `maps.apple.com`.
 *
 * Matched on the user agent, which is the only thing left that says so:
 * `navigator.platform` is deprecated, and iPadOS has reported itself as
 * "Macintosh" since iPadOS 13. That ambiguity costs nothing here — the
 * question is Apple or not, never which Apple device.
 *
 * Anything unrecognized, including a user agent that is missing entirely, is
 * treated as not-Apple.
 */
export function prefersAppleMaps(userAgent: string | null | undefined): boolean {
  if (!userAgent) return false;
  return /iphone|ipad|ipod|macintosh|mac os x/i.test(userAgent);
}

/**
 * Coordinates, but only a real pair inside the ranges they have to be in. A
 * half-filled or out-of-range pair is treated as absent, because the address
 * text is a better answer than a point in the ocean.
 */
function coordinates(address: MappableAddress): { latitude: number; longitude: number } | null {
  const { latitude, longitude } = address;
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  // Null Island is in the Gulf of Guinea, and is what an unfilled pair of
  // columns looks like far more often than it is a customer's front door.
  if (latitude === 0 && longitude === 0) return null;
  return { latitude, longitude };
}

/**
 * Apple's documented URL scheme. `ll` places the map and `q` names the pin, so
 * a property with coordinates gets the precision and the label both.
 */
function appleMapsUrl(line: string, point: { latitude: number; longitude: number } | null): string {
  const params = new URLSearchParams();
  if (point) {
    params.set('ll', `${point.latitude},${point.longitude}`);
    params.set('q', line || `${point.latitude},${point.longitude}`);
  } else {
    params.set('q', line);
  }
  return `https://maps.apple.com/?${params.toString()}`;
}

/**
 * Google's documented Maps URLs. `search` takes exactly one `query`, so unlike
 * Apple's there is no room for a point and a label both — the point wins where
 * there is one, for the reason at the top of this file.
 */
function googleMapsUrl(line: string, point: { latitude: number; longitude: number } | null): string {
  const query = point ? `${point.latitude},${point.longitude}` : line;
  const params = new URLSearchParams({ api: '1', query });
  return `https://www.google.com/maps/search/?${params.toString()}`;
}

function trimmed(value: string | null | undefined): string {
  return value?.trim() ?? '';
}
